/**
 * pane_visibility — WHICH SIDE A LIT WINDOW LIGHTS, AND WHAT STANDS IN ITS WAY (shader gaps round 6, agent J, 2026-09-30).
 * PURE (no three): the rules the area-light module seats the pooled rect lights by, and the CPU mirrors of the shader
 * terms `lib/pane_light_node.ts` builds.
 *
 * WHAT WAS WRONG. The six pooled LTC rect lights (`AtlasAreaLightsModule`) faced "the pane's thinnest axis, flipped
 * toward the player". Two leaks followed: (1) outdoors, a pane on the FAR side of a building turned to face the player
 * and lit the street on the player's side THROUGH the building (and the facing flipped as the player walked round it);
 * (2) indoors, the active interior's panes faced into the room — right — but an unshadowed rectangle also lit the
 * street, the ground and the neighbours seen through the opposite windows, far outside the room.
 *
 * THE RULES.
 *   · THE FACING LAW — a pane faces the side its light comes from, never the viewer: an EXTERIOR pane (the lit room
 *     behind it) faces OUT of its building; the ACTIVE INTERIOR's own pane (the room's glow on the glass) faces INTO the
 *     room. The side is decided by the building's centre, not the camera (`paneFacing`). three's LTC already refuses
 *     everything behind the emitter's plane, so a correct facing IS "lights only the side it faces".
 *   · THE ROOM LAW — an interior pane's light is confined to its OWN building's volume (the interior shell's box, fading
 *     to 0 across the wall's thickness): `boxWeight`, one smooth box test in the light's shader.
 *   · THE LIGHT-SET IDENTITY LAW is untouched: the pool's size and class are fixed for the session; only uniforms and
 *     transforms change (the mask and the occluders are uniforms of each pooled light).
 *   · THE OCCLUDER TERM (a few bounded visibility samples, never one centre ray): the rectangle is sampled at FOUR
 *     stratified points; each point's visibility past up to `PANE_MAX_OCCLUDERS` people (vertical capsules) is weighted
 *     by the UNSHADOWED integrand at that point (receiver cosine × emitter cosine / r²) — the ratio estimator of Heitz,
 *     Hill & McGuire 2018 (`S = U · Σwᵥ / Σw`) with a deterministic 2 × 2 stratification. The LTC value stays the
 *     unshadowed U; the ratio only scales it. A receiver inside a capsule's own body is not shadowed by it (a person
 *     never shadows their own skin, nor the floor under their feet).
 */

export type V3 = [number, number, number];
export type PaneRole = 'exterior' | 'interior';

/** Pool + budget constants (the module and the shader read these). */
export const PANE_MAX_OCCLUDERS = 4;
/** A person as a vertical capsule: radius (m) and the ends above the feet (m). */
export const PANE_PERSON_R = 0.28;
export const PANE_PERSON_Y0 = 0.3;
export const PANE_PERSON_Y1 = 1.55;
/**
 * The occluder term is evaluated only for receivers within this distance of the pane (m) — a per-fragment branch that
 * bounds the cost to the fragments near a window (the LTC value past it is small, and three evaluates every rect light
 * for every lit fragment whatever its distance).
 */
export const PANE_SHADOW_RANGE_M = 20;
/**
 * Only the pool's first slots (the panes NEAREST the player — the scan seats them in distance order) carry the
 * occluder term: every pooled light's code is compiled into EVERY lit material's program, so the term is budgeted by
 * slots, not by all six.
 */
export const PANE_SHADOW_SLOTS = 2;
/** People farther than this from a pane never occlude it (m). */
export const PANE_OCCLUDER_RANGE_M = 14;
/**
 * The room box's fade (m): the weight is 1 up to the box's faces and falls to 0 over this much beyond them — the
 * thickness of the shell's visual walls (0.16 m) plus a little, so nothing past a wall's OUTER face is lit (a symmetric
 * fade about the face put a 0.5 halo round the building's outside: the bed's first picture).
 */
export const PANE_ROOM_FADE_M = 0.2;
/** The four stratified rectangle samples (fractions of the half extents). */
export const PANE_SAMPLES: ReadonlyArray<[number, number]> = [[-0.5, -0.5], [0.5, -0.5], [-0.5, 0.5], [0.5, 0.5]];

/**
 * A pane's role for the current player: outdoors every pane is an EXTERIOR pane; inside a building only that
 * interior's own panes take part (the Render-wave-1 eligibility), as INTERIOR panes; everything else is out.
 */
export function paneRole(paneInteriorId: string | null, activeInteriorId: string | null): PaneRole | null {
  if (activeInteriorId === null) return paneInteriorId === null ? 'exterior' : null;
  return paneInteriorId === activeInteriorId ? 'interior' : null;
}

/**
 * THE FACING LAW. `axis` is the pane's unit normal up to a sign (its thinnest local axis); `centre` its building's
 * centre (same frame as `pane`). EXTERIOR → the sign pointing away from the centre; INTERIOR → toward it. When the
 * centre lies in the pane's own plane (|d| < 0.05 m — a free-standing pane) the sign is ambiguous: returns null (the
 * caller keeps the pane unseated rather than guess a side).
 */
export function paneFacing(pane: V3, axis: V3, centre: V3, role: PaneRole): V3 | null {
  const d = (centre[0] - pane[0]) * axis[0] + (centre[1] - pane[1]) * axis[1] + (centre[2] - pane[2]) * axis[2];
  if (!Number.isFinite(d) || Math.abs(d) < 0.05) return null;
  const toward = d > 0 ? 1 : -1;
  const s = role === 'interior' ? toward : -toward;
  return [axis[0] * s, axis[1] * s, axis[2] * s];
}

/**
 * THE FACING LAW outdoors, where no building centre is known (an instanced settlement batch carries only the glass):
 * the glass's AUTHORED front (its mean vertex normal, unit) points out of its building, so the building lies behind
 * it — the facing is `paneFacing` with the centre one metre behind the front. An authored front that does not agree
 * with the pane's axis (|front · axis| < `minAlong` — a glass set wrapping several walls, a lamp's globe) is no front:
 * null, the pane stays unseated.
 */
export function paneFrontFacing(pane: V3, axis: V3, front: V3, minAlong = 0.5): V3 | null {
  const along = front[0] * axis[0] + front[1] * axis[1] + front[2] * axis[2];
  if (!Number.isFinite(along) || Math.abs(along) < minAlong) return null;
  return paneFacing(pane, axis, [pane[0] - front[0], pane[1] - front[1], pane[2] - front[2]], 'exterior');
}

/** Does a pane facing `n` from `pane` light a point `p` at all (three's LTC half-space: in front of the plane)? */
export function paneLightsPoint(pane: V3, n: V3, p: V3): boolean {
  return (p[0] - pane[0]) * n[0] + (p[1] - pane[1]) * n[1] + (p[2] - pane[2]) * n[2] > 0;
}

/** A building box: centre (x, z), floor y, height, half extents in its own frame, three's yaw (rotation.y). */
export interface PaneBox { cx: number; cz: number; y0: number; h: number; hx: number; hz: number; yaw: number }

const smoothstep = (e0: number, e1: number, x: number): number => { const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); };

/**
 * THE ROOM LAW (CPU mirror of the shader's box term): 1 inside the box and ON its faces, falling to 0 over `fade`
 * beyond each face. The building frame is three's: world = (c·lx + s·lz, −s·lx + c·lz), so local = (c·dx − s·dz,
 * s·dx + c·dz).
 */
export function boxWeight(p: V3, box: PaneBox, fade = PANE_ROOM_FADE_M): number {
  const c = Math.cos(box.yaw), s = Math.sin(box.yaw);
  const dx = p[0] - box.cx, dz = p[2] - box.cz;
  const lx = c * dx - s * dz, lz = s * dx + c * dz;
  const wx = 1 - smoothstep(box.hx, box.hx + fade, Math.abs(lx));
  const wz = 1 - smoothstep(box.hz, box.hz + fade, Math.abs(lz));
  const wy = smoothstep(box.y0 - fade, box.y0, p[1]) * (1 - smoothstep(box.y0 + box.h, box.y0 + box.h + fade, p[1]));
  return wx * wz * wy;
}

/** The allowance past the shell's half extents (m): the visual walls stand ON the half extent, their inner faces inside it. */
export const PANE_ROOM_PAD_M = 0.02;

/**
 * The room box of an interior (the module and the harness share it): the shell's half extents + `PANE_ROOM_PAD_M`, from
 * just under the floor to just over the top floor; the weight then fades to 0 across the wall (`PANE_ROOM_FADE_M`).
 */
export function roomBoxFor(x: number, z: number, yaw: number, groundY: number, hw: number, hd: number, height: number): PaneBox {
  const pad = PANE_ROOM_PAD_M;
  return { cx: x, cz: z, y0: groundY - 0.05, h: height + 0.1, hx: hw + pad, hz: hd + pad, yaw };
}

/** A person as a vertical capsule: axis at (x, z) from y0 to y1, radius r. */
export interface Capsule { x: number; z: number; y0: number; y1: number; r: number }

/** A standing person at feet (x, y, z) as the capsule the shader tests. */
export function personCapsule(x: number, y: number, z: number): Capsule {
  return { x, z, y0: y + PANE_PERSON_Y0, y1: y + PANE_PERSON_Y1, r: PANE_PERSON_R };
}

/**
 * How much of the segment P → Q passes a vertical capsule (1 clear, 0 blocked), softened: the closest approach is
 * taken at the parameter nearest the axis in plan (exact for the vertical capsule's side, an approximation near its
 * caps), and the ramp widens with the distance from the receiver (a penumbra cone, iq's soft-shadow shape). A
 * receiver inside the capsule's own body (+0.25 m) is never occluded by it.
 */
export function capsuleTransmit(p: V3, q: V3, cap: Capsule): number {
  const phx = p[0] - cap.x, phz = p[2] - cap.z;
  if (Math.hypot(phx, phz) < cap.r + 0.25 && p[1] > cap.y0 - 0.55 && p[1] < cap.y1 + 0.55) return 1; // its own body / its feet
  const dx = q[0] - p[0], dy = q[1] - p[1], dz = q[2] - p[2];
  const a = dx * dx + dz * dz;
  const s = a > 1e-9 ? Math.min(1, Math.max(0, -(phx * dx + phz * dz) / a)) : 0;
  const hx = phx + dx * s, hz = phz + dz * s;
  const y = p[1] + dy * s;
  const dv = Math.max(cap.y0 - y, y - cap.y1, 0);
  const dist = Math.sqrt(hx * hx + hz * hz + dv * dv) - cap.r;
  const t = s * Math.sqrt(a + dy * dy);
  const w = 0.03 + 0.05 * t;
  return smoothstep(-w, w, dist);
}

/** A rect light in world axes: centre, unit emitter normal (the side it lights), half extents as vectors. */
export interface PaneRect { c: V3; n: V3; hw: V3; hh: V3 }

/**
 * The ratio-estimator visibility (CPU mirror): Σₖ wₖ·vₖ / Σₖ wₖ over the four stratified rectangle points, wₖ the
 * unshadowed integrand (receiver cosine × emitter cosine / max(r², A/4)), vₖ the product of every occluder's
 * transmission along P → qₖ. 1 when nothing weighs (the LTC is then 0 anyway) or nothing occludes.
 */
export function rectVisibilityCpu(p: V3, nR: V3, rect: PaneRect, occ: readonly Capsule[]): number {
  const area = 4 * Math.hypot(rect.hw[1] * rect.hh[2] - rect.hw[2] * rect.hh[1], rect.hw[2] * rect.hh[0] - rect.hw[0] * rect.hh[2], rect.hw[0] * rect.hh[1] - rect.hw[1] * rect.hh[0]);
  let W = 0, V = 0;
  for (const [a, b] of PANE_SAMPLES) {
    const q: V3 = [rect.c[0] + rect.hw[0] * a + rect.hh[0] * b, rect.c[1] + rect.hw[1] * a + rect.hh[1] * b, rect.c[2] + rect.hw[2] * a + rect.hh[2] * b];
    const d: V3 = [q[0] - p[0], q[1] - p[1], q[2] - p[2]];
    const r2 = d[0] * d[0] + d[1] * d[1] + d[2] * d[2], r = Math.sqrt(r2) || 1e-9;
    const cosR = Math.max(0, (nR[0] * d[0] + nR[1] * d[1] + nR[2] * d[2]) / r);
    const cosE = Math.max(0, -(rect.n[0] * d[0] + rect.n[1] * d[1] + rect.n[2] * d[2]) / r);
    const w = cosR * cosE / Math.max(r2, area / 4);
    let v = 1;
    for (const c of occ) v *= capsuleTransmit(p, q, c);
    W += w; V += w * v;
  }
  return W > 1e-12 ? V / W : 1;
}

/**
 * A reference visibility by brute force (n × n points over the rectangle, the same weighting, hard occlusion) — the
 * harness's oracle for the four-sample estimate.
 */
export function rectVisibilityReference(p: V3, nR: V3, rect: PaneRect, occ: readonly Capsule[], n = 24): number {
  let W = 0, V = 0;
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) {
    const a = (i + 0.5) / n - 0.5, b = (j + 0.5) / n - 0.5;
    const q: V3 = [rect.c[0] + rect.hw[0] * 2 * a + rect.hh[0] * 2 * b, rect.c[1] + rect.hw[1] * 2 * a + rect.hh[1] * 2 * b, rect.c[2] + rect.hw[2] * 2 * a + rect.hh[2] * 2 * b];
    const d: V3 = [q[0] - p[0], q[1] - p[1], q[2] - p[2]];
    const r2 = d[0] * d[0] + d[1] * d[1] + d[2] * d[2], r = Math.sqrt(r2) || 1e-9;
    const cosR = Math.max(0, (nR[0] * d[0] + nR[1] * d[1] + nR[2] * d[2]) / r);
    const cosE = Math.max(0, -(rect.n[0] * d[0] + rect.n[1] * d[1] + rect.n[2] * d[2]) / r);
    const w = cosR * cosE / Math.max(r2, 1e-6);
    let v = 1;
    for (const c of occ) {
      // hard test: the segment's true closest distance to the capsule axis segment (sampled finely along the ray)
      let minD = Infinity;
      for (let k = 1; k < 64; k++) {
        const s = k / 64, x = p[0] + d[0] * s, y = p[1] + d[1] * s, z = p[2] + d[2] * s;
        const cy = Math.min(c.y1, Math.max(c.y0, y));
        minD = Math.min(minD, Math.hypot(x - c.x, y - cy, z - c.z));
      }
      const own = Math.hypot(p[0] - c.x, p[2] - c.z) < c.r + 0.25 && p[1] > c.y0 - 0.55 && p[1] < c.y1 + 0.55;
      if (!own && minD < c.r) v = 0;
    }
    W += w; V += w * v;
  }
  return W > 1e-12 ? V / W : 1;
}

/** The people who may occlude a pane: within range, nearest first, at most the budget. */
export function pickOccluders<T extends { x: number; z: number }>(paneX: number, paneZ: number, people: readonly T[], max = PANE_MAX_OCCLUDERS, range = PANE_OCCLUDER_RANGE_M): T[] {
  const r2 = range * range;
  return people.map((p) => ({ p, d2: (p.x - paneX) ** 2 + (p.z - paneZ) ** 2 })).filter((e) => e.d2 <= r2).sort((a, b) => a.d2 - b.d2).slice(0, max).map((e) => e.p);
}
